import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Button, Typography, Box } from "@saviynt/design-system";
import { sendKeepalive } from "../../utilities/api/getDataFromApi";
import { GetAuth } from "../../contexts/AuthProvider";
import "./SessionTimeoutModal.css";

/** Prompt the user 1 minute before session expiry. */
const PROMPT_BEFORE_MS = 60_000;

/**
 * Read the user_session cookie set by the backend on every authenticated
 * request and keepalive.  Returns { timeoutMs, lastRefreshedAt }.
 */
function getSessionConfig() {
  const DEFAULT_TIMEOUT_MS = 300_000; // 5 minutes fallback
  try {
    const match = document.cookie
      .split("; ")
      .find((row) => row.startsWith("user_session="));
    if (match) {
      const value = decodeURIComponent(match.split("=")[1]);
      const parsed = JSON.parse(value);
      return {
        timeoutMs: (parsed.timeoutMins || 5) * 60 * 1000,
        lastRefreshedAt: parsed.lastRefreshedAt || 0,
      };
    }
  } catch {
    // fall through
  }
  return { timeoutMs: DEFAULT_TIMEOUT_MS, lastRefreshedAt: 0 };
}

/**
 * Extract the sessionId claim from the access_token JWT cookie.
 * Returns null if the cookie is missing or unparseable.
 */
function getSessionIdFromCookie() {
  try {
    const match = document.cookie
      .split("; ")
      .find((row) => row.startsWith("access_token="));
    if (match) {
      const jwt = match.split("=")[1];
      const payload = JSON.parse(atob(jwt.split(".")[1]));
      return payload.sessionId || null;
    }
  } catch {
    // fall through
  }
  return null;
}

/**
 * SessionTimeoutModal — shows a "Session Expiring" dialog 1 minute before
 * the backend session expires.  Two buttons: Continue Session (outlined) and
 * Log Out (filled, default).  Auto-logs-out when the countdown reaches zero.
 *
 * Design principles:
 * 1. Single source of truth = backend session expiry (via user_session cookie's
 *    lastRefreshedAt + timeoutMs).  The BE extends the session on every
 *    authenticated API call AND on keepalive to now() + sessionTimeout.
 * 2. Wall-clock based — uses Date.now() on every check, so background-tab
 *    throttling of setInterval cannot prevent logout.  A visibilitychange
 *    handler catches the "tab was hidden for N minutes" case immediately.
 * 3. Per-window — stores this window's sessionId (from the JWT) at mount and
 *    tracks its own lastRefreshedAt.  When incognito windows share cookies,
 *    another window's API call may overwrite the access_token/user_session
 *    cookies with a different session's values.  This component detects the
 *    mismatch and falls back to its own tracked refresh time.
 */
export default function SessionTimeoutModal() {
  const auth = GetAuth();
  const [showModal, setShowModal] = useState(false);
  const [remainingMs, setRemainingMs] = useState(0);

  // This window's session identity — captured once at mount from the JWT.
  const windowSessionIdRef = useRef(null);
  // This window's own lastRefreshedAt — so we can still compute expiry when the
  // shared cookie has been overwritten by another window's session.
  const myLastRefreshedAtRef = useRef(Date.now());
  const showModalRef = useRef(false);
  const timerIdRef = useRef(null);

  // Capture this window's sessionId on mount (and whenever the token changes).
  useEffect(() => {
    const sid = getSessionIdFromCookie();
    if (sid) {
      windowSessionIdRef.current = sid;
    }
    // Also snapshot the initial lastRefreshedAt from the cookie
    const { lastRefreshedAt } = getSessionConfig();
    if (lastRefreshedAt) {
      myLastRefreshedAtRef.current = lastRefreshedAt;
    }
  }, [auth.token]);

  /**
   * Perform logout: end session on backend, clear auth state, reload.
   * @param {"timeout"|"logout"} reason
   */
  const doLogout = useCallback(
    async (reason = "logout") => {
      showModalRef.current = false;
      setShowModal(false);
      clearInterval(timerIdRef.current);
      try {
        if (auth.user) {
          const ctrl = new AbortController();
          const tid = setTimeout(() => ctrl.abort(), 3000);
          await fetch("/api/v1/bg/session-end", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username: auth.user, reason }),
            signal: ctrl.signal,
          });
          clearTimeout(tid);
        }
      } catch {
        // best-effort — backend prune handles cleanup
      }
      auth.logOut();
      window.location.reload();
    },
    [auth],
  );

  /**
   * Compute remaining time from wall-clock.  Uses the backend's
   * lastRefreshedAt when the cookie belongs to this window's session.
   * When another window's session overwrote the cookie, falls back to
   * this window's own tracked refresh time (myLastRefreshedAtRef).
   */
  const computeRemaining = useCallback(() => {
    const { timeoutMs, lastRefreshedAt } = getSessionConfig();

    // Per-window guard: if the cookie's JWT sessionId matches ours, use the
    // cookie's lastRefreshedAt (it was updated by our own API calls / keepalive).
    // If it doesn't match (another window overwrote it), use our own tracked time.
    let effectiveRefresh = myLastRefreshedAtRef.current;

    if (windowSessionIdRef.current) {
      const cookieSessionId = getSessionIdFromCookie();
      if (!cookieSessionId || cookieSessionId === windowSessionIdRef.current) {
        // Cookie belongs to us (or no JWT in cookie yet) — use its lastRefreshedAt
        if (lastRefreshedAt) {
          effectiveRefresh = lastRefreshedAt;
          myLastRefreshedAtRef.current = lastRefreshedAt; // keep our tracker in sync
        }
      }
      // else: cookie belongs to another window — use our tracked value (stale for us,
      // which is correct: our session hasn't been extended, it should be expiring)
    } else {
      // No windowSessionId yet (mount in progress) — use cookie value
      if (lastRefreshedAt) {
        effectiveRefresh = lastRefreshedAt;
      }
    }

    return timeoutMs - (Date.now() - effectiveRefresh);
  }, []);

  /**
   * Core check — called by setInterval AND by visibilitychange.
   * Uses wall-clock math so it's correct even if the tab was throttled.
   */
  const checkExpiry = useCallback(() => {
    const remaining = computeRemaining();

    if (remaining <= 0) {
      doLogout("timeout");
      return;
    }

    if (remaining <= PROMPT_BEFORE_MS) {
      if (!showModalRef.current) {
        showModalRef.current = true;
        setShowModal(true);
      }
      setRemainingMs(remaining);
    } else {
      // Session was extended (e.g. by an API call, or Continue) — dismiss modal.
      if (showModalRef.current) {
        showModalRef.current = false;
        setShowModal(false);
      }
    }
  }, [computeRemaining, doLogout]);

  // 1-second tick for countdown display (foreground).
  useEffect(() => {
    timerIdRef.current = setInterval(checkExpiry, 1000);
    return () => clearInterval(timerIdRef.current);
  }, [checkExpiry]);

  // Wall-clock safety net: when the tab becomes visible after being
  // backgrounded (where setInterval may have been throttled to ~1/min or
  // frozen entirely), immediately recompute from wall-clock and act.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkExpiry();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [checkExpiry]);

  /** "Continue Session" — keepalive extends to now()+sessionTimeout on BE. */
  const handleContinue = useCallback(async () => {
    const result = await sendKeepalive("continue");
    if (result?.status === "ok") {
      // Backend extended our session. The keepalive response updated the
      // access_token cookie and the backend wrote a fresh user_session cookie
      // with lastRefreshedAt = now.  Update our own tracker immediately so
      // the next checkExpiry() sees the extension even if the cookie was
      // overwritten by another window between now and the next tick.
      myLastRefreshedAtRef.current = Date.now();
      showModalRef.current = false;
      setShowModal(false);
    } else {
      // Keepalive rejected (vault sealed or token invalid) — logout cleanly.
      doLogout("timeout");
    }
  }, [doLogout]);

  // Format remaining as M:SS
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const display = `${minutes}:${seconds.toString().padStart(2, "0")}`;

  if (!showModal) return null;

  return ReactDOM.createPortal(
    <div className="SessionTimeoutModal-overlay">
      <div className="SessionTimeoutModal-dialog">
        <Typography kind="h2" style={{ marginBottom: "0.5rem" }}>
          Session Expiring
        </Typography>
        <Typography kind="body">
          You will be logged out due to inactivity.
        </Typography>
        <Typography kind="h1" className="SessionTimeoutModal-timer">
          {display}
        </Typography>
        <Box className="SessionTimeoutModal-actions">
          <Button kind="outlined" size="medium" onClick={handleContinue}>
            Continue Session
          </Button>
          <Button kind="filled" size="medium" onClick={() => doLogout("logout")}>
            Log Out
          </Button>
        </Box>
      </div>
    </div>,
    document.body,
  );
}
